import { Info, MapPin, Wallet, Coffee, Droplets, Pill, ChevronDown } from 'lucide-react';
import { CLINIC_INFO } from '../data';
import { motion, AnimatePresence } from 'framer-motion';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

export default function PracticalInfo() {
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    document.title = "CDH Sports- og Wellness Massage - Praktisk info";
  }, []);

  const toggleFaq = (idx) => {
    setOpenFaq(openFaq === idx ? null : idx);
  };

  const faqItems = [
    {
      question: "Er massage godt mod hovedpine?",
      answer: "Ja, især fysiurgisk massage er yderst effektivt mod spændingshovedpine, der ofte stammer fra nakke og skuldre."
    },
    {
      question: "Hvor ofte bør jeg få massage?",
      answer: (
        <>
          Det afhænger af dine behov. Ved akutte problemer anbefales 1-2 gange om ugen, mens forebyggende massage typisk sker hver 4. uge.
          <br /><br />
          Det kan du dog kun selv mærke efter. Behovet er helt personligt og afhænger af din krop og situation, så det handler om at lytte til, hvad der føles rigtigt for dig.
        </>
      )
    },
    {
      question: "Må jeg få massage hvis jeg er gravid?",
      answer: "Ja! Jeg har specifik gravid-massage, der er skånsom og sikker for både dig og barnet."
    },
    {
      question: "Gør det ondt at få massage?",
      answer: "Fysiurgisk massage kan mærkes, da jeg arbejder i dybden, men jeg tilpasser altid trykket efter din smertetærskel."
    }
  ];

  const infoSections = [
    {
      title: "Generelt",
      icon: <Info className="w-8 h-8 text-blue-600" />,
      items: [
        {
          icon: <Coffee className="w-5 h-5 text-emerald-500" />,
          text: "Det ikke nødvendigt at medbringe hverken håndklæde eller lagen."
        },
        {
          icon: <Wallet className="w-5 h-5 text-emerald-500" />,
          text: "Vær opmærksom på at jeg pt. kun modtager kontant betaling eller mobilepay."
        },
        {
          icon: <Pill className="w-5 h-5 text-emerald-500" />,
          text: "Det frarådes at tage nogen form for smertestillende medicin eller piller inden massage, da din smertetærskel flyttes, og dermed kan man komme til at trykke uhensigtsmæssigt hårdt på særligt ømme eller udsatte steder."
        },
        {
          icon: <Droplets className="w-5 h-5 text-emerald-500" />,
          text: "HUSK at drikke masser af vand bagefter, så affaldsstofferne bliver skyllet ud af kroppen."
        }
      ]
    },
    {
      title: "Adr.",
      icon: <MapPin className="w-8 h-8 text-blue-600" />,
      content: "Banegårdspladsen 2, 6230 Rødekro",
      details: [
        "Klinikken er beliggende direkte på banegården.",
        "Indgangen finder du ved enden af bygningen, lige ved cykelskuret."
      ]
    }
  ];

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="relative h-[70vh] md:h-[75vh] lg:h-[80vh] flex items-center overflow-hidden pt-32 md:pt-40 lg:pt-48">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/assets/767bb60f-8ec8-4a4c-940e-ed49b89257a3/d85dacce-5da1-437b-8cd1-59f0d78c999b.jpg"
            alt="Praktisk Info"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-blue-900/60 z-10" />
        </div>

        <div className="container mx-auto px-4 relative z-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <h1 className="text-2xl md:text-4xl lg:text-5xl font-bold text-white mb-6 leading-[1.1]">
              Praktisk info
            </h1>
            <p className="text-xs sm:text-sm md:text-base text-white/90 mb-10 leading-relaxed font-light">
              Her finder du alt det praktiske du skal vide før dit besøg i klinikken.
            </p>
            <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
              <a 
                href="#info" 
                className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-3.5 lg:px-8 lg:py-4 rounded-full text-base lg:text-lg font-bold transition-all shadow-lg flex items-center justify-center"
              >
                Se praktisk info
              </a>
              <Link 
                to="/kontakt" 
                className="bg-white/10 hover:bg-white/20 backdrop-blur-md text-white border border-white/30 px-6 py-3.5 lg:px-8 lg:py-4 rounded-full text-base lg:text-lg font-bold transition-all flex items-center justify-center"
              >
                Kontakt mig
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Info Sections */}
      <section id="info" className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto space-y-8">
            {infoSections.map((section, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="p-8 md:p-12 rounded-[2.5rem] bg-gray-50 border border-gray-100 hover:bg-blue-50/30 transition-colors"
              >
                <div className="flex items-center gap-6 mb-8">
                  <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100">
                    {section.icon}
                  </div>
                  <h3 className="text-3xl font-bold text-blue-900">{section.title}</h3>
                </div>
                
                {section.items ? (
                  <div className="space-y-6">
                    {section.items.map((item, itemIdx) => (
                      <div key={itemIdx} className="flex gap-4">
                        <div className="mt-1 shrink-0">{item.icon}</div>
                        <p className="text-gray-600 leading-relaxed text-sm sm:text-base md:text-lg">{item.text}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="space-y-6">
                    <a 
                      href="https://maps.app.goo.gl/zduoinBMcwsKXgZPA"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-block text-blue-900 leading-relaxed text-xl md:text-3xl font-bold hover:text-blue-600 transition-colors"
                    >
                      {section.content}
                    </a>
                    
                    <div className="space-y-3">
                      {section.details.map((detail, detailIdx) => (
                        <div key={detailIdx} className="flex items-start gap-3">
                          <span className="text-emerald-500 font-bold text-lg">•</span>
                          <p className="text-gray-600 text-sm sm:text-base md:text-lg leading-relaxed">
                            {detail}
                          </p>
                        </div>
                      ))}
                    </div>

                    <div className="mt-6 overflow-hidden rounded-2xl border border-blue-100 shadow-sm bg-white">
                      <img 
                        src="https://assets.auxodev.dk/assets/767bb60f-8ec8-4a4c-940e-ed49b89257a3/57a0dfcd-a04f-49f7-8d91-127785757ada.jpg" 
                        alt="Klinikkens indgang ved banegården" 
                        className="w-full h-auto object-cover max-h-[400px] hover:scale-[1.02] transition-transform duration-500"
                      />
                      <div className="p-4 bg-blue-50/50 border-t border-blue-50 text-center">
                        <p className="text-blue-900 font-medium text-sm">
                          Billede af indgangen ved enden af bygningen (ved cykelskuret)
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ/Quick Questions */}
      <section className="py-24 bg-blue-900 text-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold mb-4">Ofte stillede spørgsmål</h2>
            <p className="text-blue-200 text-sm sm:text-base">Korte svar på de mest almindelige spørgsmål.</p>
          </div>

          <div className="max-w-3xl mx-auto space-y-4">
            {faqItems.map((item, idx) => {
              const isOpen = openFaq === idx;
              return (
                <div 
                  key={idx}
                  className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 overflow-hidden transition-all duration-300"
                >
                  <button
                    onClick={() => toggleFaq(idx)}
                    className="w-full flex justify-between items-center p-6 text-left font-bold text-emerald-400 hover:text-emerald-300 transition-colors focus:outline-none"
                  >
                    <span className="text-base sm:text-lg pr-4">{item.question}</span>
                    <ChevronDown 
                      className={`w-5 h-5 text-blue-200 shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} 
                    />
                  </button>
                  
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: "easeInOut" }}
                      >
                        <div className="px-6 pb-6 pt-1 border-t border-white/5 text-blue-100/80 text-[13px] sm:text-sm leading-relaxed">
                          {item.answer}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Help CTA */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4 text-center">
          <div className="bg-emerald-500 rounded-[3rem] p-12 md:p-20 text-white shadow-xl relative overflow-hidden max-w-5xl mx-auto">
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl" />
            <div className="relative z-10">
              <Info className="w-16 h-16 mx-auto mb-8 opacity-50" />
              <h2 className="text-3xl md:text-4xl font-bold mb-8">Stadig i tvivl om noget?</h2>
              <p className="text-emerald-50 mb-12 text-sm sm:text-base md:text-lg max-w-2xl mx-auto">
                Du er altid velkommen til at ringe eller skrive til mig, hvis du har spørgsmål der ikke er besvaret her på siden.
              </p>
              <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-6">
                <a href={`tel:${CLINIC_INFO.phone}`} className="bg-white text-emerald-600 px-8 py-3.5 lg:px-10 lg:py-4 rounded-full font-bold text-base lg:text-lg hover:bg-blue-50 transition-all">
                  Ring: {CLINIC_INFO.phone}
                </a>
                <a href={`mailto:${CLINIC_INFO.email}`} className="bg-emerald-600 border-2 border-white/30 text-white px-8 py-3.5 lg:px-10 lg:py-4 rounded-full font-bold text-base lg:text-lg hover:bg-emerald-700 transition-all">
                  Send e-mail
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
